import React, { useState, useEffect, useMemo } from 'react';
import { useInfinitePersons, useUpdateConnectPerson, useUnmatchVictims, useInfiniteVictimReunification, VictimReunificationGroup } from '#root/queries/persons';
import { useInfiniteIncidents, Incident } from '#root/queries/incidents';
import { useCurrentUser, useVAdminSelectedIncident } from '#root/queries/local';
import { Button, Spinner, Badge, Field, Switch, Input } from '@fluentui/react-components';
import { Person } from '#root/queries/persons';
import { Breadcrumbs } from "#root/components/Breadcrumbs";
import InfiniteDropdown from "#root/components/InfiniteDropdown";
import { ArrowClockwiseRegular, PersonAvailableFilled, PersonQuestionMarkRegular, PersonDeleteFilled, SearchRegular } from '@fluentui/react-icons';
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { PERSON_TYPES } from '#root/utils/persons';
import MatchGroup from "#root/pages/VAdmin/VictimReunification/MatchGroup";
import LoadingWrapper from "#root/components/LoadingWrapper";
import './styles.scss';
import { DataCollectionApiResponse } from "#root/queries/types";
import { useInfiniteScroll } from '#root/hooks/useInfiniteScroll';
import { showToast } from '#root/components/GlobalToaster';
import { DialogBox } from '#root/components/DialogBox';

const ITEMS_PER_PAGE = 20;
const INCIDENTS_PER_PAGE = 50;

const VictimReunification: React.FC = () => {
    const { data: currentUser } = useCurrentUser();
    const unit = currentUser?.unit;
    const [incidentFilter, setIncidentFilter] = useState('');
    const [nameSearch, setNameSearch] = useState('');
    const [showNotContactedOnly, setShowNotContactedOnly] = useState(true);
    const [selectedPublicIds, setSelectedPublicIds] = useState<Set<string>>(new Set());
    const [isSaving, setIsSaving] = useState(false);
    const [isUnmatching, setIsUnmatching] = useState(false);
    const [isUnmatchDialogOpen, setIsUnmatchDialogOpen] = useState(false);

    const { selectedIncidentId, setVAdminSelectedIncidentId } = useVAdminSelectedIncident();

    const incidentsQuery = useInfiniteIncidents({
        take: INCIDENTS_PER_PAGE,
        name: incidentFilter,
    }) as unknown as UseInfiniteQueryResult<InfiniteData<DataCollectionApiResponse<Incident>, unknown>, unknown>;

    const handleIncidentChange = (incidentId: string | null) => {
        setVAdminSelectedIncidentId(incidentId);
    };

    const { mutateAsync: updateConnectPerson } = useUpdateConnectPerson();
    const { mutateAsync: unmatchVictims } = useUnmatchVictims();

    const { data: reunificationData, isLoading, isFetching, refetch, fetchNextPage, hasNextPage } = useInfiniteVictimReunification({
        incidentId: selectedIncidentId || undefined,
        name: nameSearch || undefined,
        displayNotContactedOnly: showNotContactedOnly,
        take: ITEMS_PER_PAGE,
    });

    // Count queries using the existing persons endpoint (totalCount still works)
    const { data: matchedData } = useInfinitePersons({
        incidentId: selectedIncidentId || '',
        isMatched: true,
        type: PERSON_TYPES.PUBLIC,
        take: 1,
    });

    const { data: contactedData } = useInfinitePersons({
        incidentId: selectedIncidentId || '',
        isMatched: true,
        isConnected: true,
        type: PERSON_TYPES.PUBLIC,
        take: 1,
    });

    const listRef = useInfiniteScroll({
        isLoading,
        hasNextPage,
        onLoadMore: () => fetchNextPage()
    });

    const groups: VictimReunificationGroup[] = reunificationData?.pages.flatMap(page => page.items) ?? [];
    const matchedCount = matchedData?.pages[0]?.totalCount ?? 0;
    const contactedCount = contactedData?.pages[0]?.totalCount ?? 0;

    // Build a mapping from publicId -> victimId for actions
    const publicToVictimMap = useMemo(() => {
        const map = new Map<string, string>();
        for (const group of groups) {
            if (!group.victim.id) continue;
            for (const pub of group.matchedPublicPersons) {
                if (pub.id) map.set(pub.id, group.victim.id);
            }
        }
        return map;
    }, [groups]);

    useEffect(() => {
        setSelectedPublicIds(new Set());
        if (listRef.current) {
            listRef.current.scrollTop = 0;
        }
        refetch();
    }, [selectedIncidentId, nameSearch, showNotContactedOnly, refetch]);

    const handleRefresh = () => {
        refetch();
    };

    const handleSelectPublicPerson = (pub: Person) => {
        if (!pub.id) return;
        setSelectedPublicIds(prev => {
            const next = new Set(prev);
            if (next.has(pub.id!)) {
                next.delete(pub.id!);
            } else {
                next.add(pub.id!);
            }
            return next;
        });
    };

    const handleSaveToggle = async () => {
        setIsSaving(true);
        try {
            await updateConnectPerson({
                ids: Array.from(selectedPublicIds),
                isConnected: true
            });

            const count = selectedPublicIds.size;
            setSelectedPublicIds(new Set());
            showToast(`${count > 1 ? 'Contacts' : 'Contact'} saved successfully`, 'success');
            refetch();
        } catch (error) {
            showToast("Failed to update persons", 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleConfirmUnmatch = async () => {
        setIsUnmatching(true);
        try {
            // Group selected public IDs by their victim
            const victimToPublicIds = new Map<string, string[]>();
            Array.from(selectedPublicIds).forEach(pubId => {
                const victimId = publicToVictimMap.get(pubId);
                if (victimId) {
                    const existing = victimToPublicIds.get(victimId) || [];
                    existing.push(pubId);
                    victimToPublicIds.set(victimId, existing);
                }
            });

            await Promise.all(
                Array.from(victimToPublicIds.entries()).map(([victimId, publicIds]) =>
                    unmatchVictims({ victimId, publicIds })
                )
            );

            showToast(`Unmatch successful for ${selectedPublicIds.size} match(es)`, 'success');
            setSelectedPublicIds(new Set());
            refetch();
        } catch (error) {
            showToast("Failed to unmatch victim(s)", 'error');
        } finally {
            setIsUnmatching(false);
            setIsUnmatchDialogOpen(false);
        }
    };

    const hasSelection = selectedPublicIds.size > 0;

    // Check if all selected public persons are already contacted
    const isCurrentlyContacted = hasSelection && Array.from(selectedPublicIds).every(pubId => {
        const group = groups.find(g => g.matchedPublicPersons.some(p => p.id === pubId));
        const pub = group?.matchedPublicPersons.find(p => p.id === pubId);
        return pub?.isConnected === true;
    });

    return (
        <div className="victim-reunification-container layout__body--paddings">
            <div className="victim-reunification">
                <div className="emc-page-header">
                    <div className="emc-page-header__top-row">
                        <div className="emc-page-header__breadcrumbs">
                            <Breadcrumbs />
                        </div>
                        <div className="emc-page-header__props">
                            <Field label="Incident" className='victim-reunification__incident'>
                                <InfiniteDropdown
                                    value={selectedIncidentId || ''}
                                    onChange={(_, data) => handleIncidentChange(data.value)}
                                    onFilterChange={setIncidentFilter}
                                    queryResult={incidentsQuery}
                                    itemToOption={(incident: Incident) => ({
                                        value: incident.id ?? '',
                                        text: incident.name ?? ''
                                    })}
                                    placeholder="All Incidents"
                                    clearable
                                />
                            </Field>
                            <Field>
                                <Switch
                                    label="Display Not Contacted Only"
                                    checked={showNotContactedOnly}
                                    onChange={(_, data) => setShowNotContactedOnly(data.checked)}
                                />
                            </Field>
                            <Badge appearance="filled" color="success" size="extra-large" className="matched-badge">
                                Matched: {matchedCount}
                            </Badge>
                            <Badge appearance="filled" color="informative" size="extra-large" className="contacted-badge">
                                Contacted: {contactedCount}
                            </Badge>
                            <Button
                                appearance="primary"
                                shape="circular"
                                icon={<ArrowClockwiseRegular />}
                                onClick={handleRefresh} />
                        </div>
                    </div>
                </div>

                <div className="victim-reunification__controls">
                    <Field label="Search by Name" className="victim-reunification__search">
                        <Input
                            contentBefore={<SearchRegular />}
                            value={nameSearch}
                            onChange={(_, data) => setNameSearch(data.value)}
                            placeholder="Search..."
                        />
                    </Field>
                </div>

                <h3 className="page-heading">
                    Victim Reunification <div className="divider" />
                </h3>

                <div className="tab-container">
                    <LoadingWrapper
                        isLoading={isLoading}
                        isEmpty={!isLoading && groups.length === 0}
                        emptyMessage={showNotContactedOnly
                            ? 'There are no uncontacted victims at this time.'
                            : 'There are no matched victims at this time.'}
                        emptyIcon={<PersonQuestionMarkRegular />}
                    >
                        <div ref={listRef} className="tab-content">
                            {groups.map((group, idx) => (
                                <MatchGroup
                                    key={group.victim.id ?? idx}
                                    victim={group.victim}
                                    matchedPublicPersons={group.matchedPublicPersons}
                                    selectedIds={selectedPublicIds}
                                    onSelectPublicPerson={handleSelectPublicPerson}
                                    unit={unit}
                                />
                            ))}
                            {isFetching && (
                                <div className="match-victims__column-loading">
                                    <Spinner size="tiny" />
                                </div>
                            )}
                        </div>
                    </LoadingWrapper>
                </div>

                {hasSelection && (
                    <div className="emc-page-footer">
                        <Button
                            className="red-button"
                            icon={isUnmatching ? <Spinner size="tiny" /> : <PersonDeleteFilled />}
                            appearance="primary"
                            disabled={isUnmatching}
                            onClick={() => setIsUnmatchDialogOpen(true)}
                        >
                            {isUnmatching ? <Spinner size="tiny" /> : 'Unmatch Selected'}
                        </Button>
                        {!isCurrentlyContacted && (
                            <Button
                                icon={isSaving ? <Spinner size="tiny" /> : <PersonAvailableFilled />}
                                appearance="primary"
                                disabled={isSaving}
                                onClick={handleSaveToggle}
                            >
                                {isSaving ? 'Saving...' : 'Save as Contacted'}
                            </Button>
                        )}
                    </div>
                )}

                <DialogBox
                    open={isUnmatchDialogOpen}
                    onOpenChange={setIsUnmatchDialogOpen}
                    title="Unmatch Victim"
                    primaryButtonText="Unmatch"
                    onPrimaryButtonClick={handleConfirmUnmatch}
                    isPrimaryButtonLoading={isUnmatching}
                >
                    Are you sure you want to unmatch the selected victim(s)?
                </DialogBox>
            </div>
        </div>
    );
};

export default VictimReunification;
